/**
 * Admin Chat v1 — room list + thread, aligned with Teachers app (sisr-teachers-rn):
 * POST conversations.sync, GET/POST messages per selected conversationId.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Alert,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
  List,
  ListItemButton,
  ListItemText,
} from '@mui/material';
import ForumOutlinedIcon from '@mui/icons-material/ForumOutlined';
import axios from 'axios';
import { useSelector } from 'react-redux';

const API = process.env.REACT_APP_API_URL
  ? String(process.env.REACT_APP_API_URL).replace(/\/$/, '')
  : 'https://servermain.sisrevolution.com';

const PAGE_LIMIT = 50;

function schoolConversationId(schoolID) {
  const s = String(schoolID || '').trim();
  if (!s) return '';
  return `${s}:school`;
}

/** Fallback rooms when sync fails or returns nothing — real Chat v1 ids (Teachers catalog pattern). */
function fallbackChatRooms(schoolID) {
  const sid = String(schoolID || '').trim();
  if (!sid) return [];
  return [
    {
      conversationId: `${sid}:school`,
      title: 'School chat',
      subtitle: 'Everyone at your school',
      type: 'school',
      unreadCount: 0,
    },
    {
      conversationId: `${sid}:role_teachers`,
      title: 'Teachers',
      subtitle: 'Teachers channel',
      type: 'role',
      unreadCount: 0,
    },
    {
      conversationId: `${sid}:role_admins`,
      title: 'Administration',
      subtitle: 'School administrators',
      type: 'role',
      unreadCount: 0,
    },
  ];
}

function mapSyncRows(rows, schoolID) {
  if (!Array.isArray(rows) || rows.length === 0) return fallbackChatRooms(schoolID);
  const out = rows
    .map((row, index) => ({
      conversationId: String(row.conversationId || ''),
      title: row.title || 'Conversation',
      subtitle:
        typeof row.lastMessageText === 'string' && row.lastMessageText.trim() !== ''
          ? row.lastMessageText
          : row.lastMessageAt
            ? new Date(Number(row.lastMessageAt)).toLocaleString()
            : 'No messages yet',
      type: row.type || 'school',
      unreadCount: Math.max(0, Number(row.unreadCount) || 0),
      order: index,
    }))
    .filter((r) => r.conversationId);
  return out.length ? out : fallbackChatRooms(schoolID);
}

function adminDisplayName(admin) {
  const ln = admin?.lastname;
  const on = admin?.othernames;
  if (ln || on) return `${ln || ''} ${on || ''}`.trim();
  return admin?.fullname || 'School Admin';
}

function mapV1Item(raw) {
  const attachments = Array.isArray(raw.attachments) ? raw.attachments : [];
  const a0 = attachments[0];
  return {
    messageId: String(raw.messageId || ''),
    clientMessageId: String(raw.clientMessageId || ''),
    senderid: String(raw.senderId || ''),
    sender: String(raw.senderName || ''),
    senderName: String(raw.senderName || ''),
    message: String(raw.body || ''),
    text: String(raw.body || ''),
    sentTime:
      typeof raw.createdAt === 'number'
        ? raw.createdAt
        : Number(raw.createdAt) || Date.now(),
    attachments,
    mediaUrl: a0?.url ? String(a0.url) : '',
    mediaType: a0?.type === 'image' ? 'image' : 'file',
    fileName: a0?.fileName ? String(a0.fileName) : '',
  };
}

function getDayKey(m) {
  const t = m.sentTime;
  if (t != null && !Number.isNaN(Number(t))) {
    return new Date(Number(t)).toISOString().slice(0, 10);
  }
  return '';
}

function formatDateSeparatorLabel(dayKey) {
  if (!dayKey || dayKey.length < 10) return '';
  const y = parseInt(dayKey.slice(0, 4), 10);
  const mo = parseInt(dayKey.slice(5, 7), 10) - 1;
  const day = parseInt(dayKey.slice(8, 10), 10);
  const t = new Date(y, mo, day).getTime();
  const startOf = (d) => new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
  const diffDays = Math.round((startOf(new Date()) - startOf(new Date(t))) / 86400000);
  if (diffDays === 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  return new Date(t).toLocaleDateString(undefined, {
    weekday: 'long',
    month: 'short',
    day: 'numeric',
  });
}

function mergeByMessageId(existing, incoming) {
  const map = new Map();
  for (const m of existing) {
    const k = m.messageId || m.clientMessageId || `${m.sentTime}-${m.senderid}`;
    if (k) map.set(String(k), m);
  }
  for (const m of incoming) {
    const k = m.messageId || m.clientMessageId || `${m.sentTime}-${m.senderid}`;
    if (k) map.set(String(k), { ...map.get(String(k)), ...m });
  }
  return [...map.values()].sort((a, b) => (a.sentTime || 0) - (b.sentTime || 0));
}

function buildListRows(sorted, userId, displayName) {
  const rows = [];
  const uid = String(userId || '');
  const myName = (displayName || '').trim();

  for (let i = 0; i < sorted.length; i++) {
    const m = sorted[i];
    const prev = i > 0 ? sorted[i - 1] : null;
    const dk = getDayKey(m);

    if (i === 0 && dk) {
      rows.push({ kind: 'date', key: `d0-${dk}`, label: formatDateSeparatorLabel(dk) });
    } else if (prev && dk && getDayKey(prev) !== dk) {
      rows.push({ kind: 'date', key: `d-${dk}-${i}`, label: formatDateSeparatorLabel(dk) });
    }

    const sid = String(m.senderid || '');
    const own =
      (uid && sid === uid) ||
      (myName && (m.sender || m.senderName || '').trim() === myName);

    rows.push({
      kind: 'msg',
      key: m.messageId || m.clientMessageId || `m-${i}-${m.sentTime}`,
      m,
      own,
    });
  }
  return rows;
}

function dispatchLivechatUnreadRefresh() {
  if (
    typeof document !== 'undefined' &&
    typeof CustomEvent !== 'undefined' &&
    typeof document.dispatchEvent === 'function'
  ) {
    document.dispatchEvent(new CustomEvent('sisr-livechat-unread-refresh'));
  }
}

async function markV1Read(apiItems, conversationId, schoolID, userId) {
  const ids = apiItems
    .filter((x) => String(x.senderId) !== String(userId) && x.messageId)
    .map((x) => x.messageId);
  if (ids.length === 0) return;
  try {
    await axios.post(`${API}/api/chat/v1/messages.read`, {
      conversationId,
      schoolID,
      userId,
      messageIds: ids,
    });
    dispatchLivechatUnreadRefresh();
  } catch {
    /* optional */
  }
}

export default function Livechat() {
  const admin = useSelector((state) => state.admin) || {};
  const userId = admin._id != null ? String(admin._id) : '';
  const schoolID = admin.schoolID != null ? String(admin.schoolID) : '';
  const myName = useMemo(() => adminDisplayName(admin), [admin]);

  const [conversations, setConversations] = useState([]);
  const [listLoading, setListLoading] = useState(true);
  const [selectedId, setSelectedId] = useState('');

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState(null);
  const [nextCursor, setNextCursor] = useState(null);
  const [hasMoreOlder, setHasMoreOlder] = useState(false);
  const [draft, setDraft] = useState('');

  const scrollRef = useRef(null);

  const conversationId = selectedId;

  const selectedRoom = useMemo(
    () => conversations.find((c) => c.conversationId === selectedId),
    [conversations, selectedId]
  );

  const scrollBottom = useCallback(() => {
    const el = scrollRef.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, []);

  const refreshConversationList = useCallback(async () => {
    if (!schoolID || !userId) return;
    try {
      const syncRes = await axios.post(`${API}/api/chat/v1/conversations.sync`, {
        schoolID,
        userId,
        limit: 50,
      });
      const syncRows = syncRes.data?.items || syncRes.data?.conversations || [];
      const roomList = mapSyncRows(syncRows, schoolID);
      setConversations(roomList.length ? roomList : fallbackChatRooms(schoolID));
    } catch {
      /* keep existing rooms */
    }
  }, [schoolID, userId]);

  /** Load conversation list (Teachers app: fetchStaffChatConversations → conversations.sync). */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!schoolID || !userId) {
        setListLoading(false);
        setConversations([]);
        return;
      }
      setListLoading(true);
      try {
        const res = await axios.post(`${API}/api/chat/v1/conversations.sync`, {
          schoolID,
          userId,
          limit: 50,
        });
        if (cancelled) return;
        const apiRows = res.data?.items || res.data?.conversations || [];
        const mapped = mapSyncRows(apiRows, schoolID);
        const roomList = mapped.length ? mapped : fallbackChatRooms(schoolID);
        setConversations(roomList);
        setSelectedId((prev) => {
          if (prev && roomList.some((m) => m.conversationId === prev)) return prev;
          const primary =
            roomList.find((m) => m.conversationId === schoolConversationId(schoolID))?.conversationId ||
            roomList[0]?.conversationId ||
            '';
          return primary;
        });
      } catch {
        if (!cancelled) {
          const fb = fallbackChatRooms(schoolID);
          setConversations(fb);
          setSelectedId((prev) => prev || fb[0]?.conversationId || schoolConversationId(schoolID));
        }
      } finally {
        if (!cancelled) setListLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolID, userId]);

  const fetchLatest = useCallback(
    async ({ silent } = {}) => {
      if (!schoolID || !userId || !conversationId) return;
      if (!silent) setRefreshing(true);
      try {
        const enc = encodeURIComponent(conversationId);
        const res = await axios.get(`${API}/api/chat/v1/conversations/${enc}/messages`, {
          params: { schoolID, userId, limit: PAGE_LIMIT },
        });
        const raw = res.data?.items || [];
        const mapped = raw.map(mapV1Item);
        setItems((prev) => mergeByMessageId(prev, mapped));
        if (!silent) {
          setHasMoreOlder(Boolean(res.data?.hasMore));
          setNextCursor(res.data?.nextCursor != null ? Number(res.data.nextCursor) : null);
        }
        setError(null);
        await markV1Read(raw, conversationId, schoolID, userId);
        await refreshConversationList();
        if (!silent) setTimeout(scrollBottom, 50);
      } catch (e) {
        const msg = e?.response?.data?.message || e.message || 'Failed to load chat';
        setError(msg);
      } finally {
        if (!silent) setRefreshing(false);
      }
    },
    [conversationId, schoolID, userId, scrollBottom, refreshConversationList]
  );

  const loadOlder = useCallback(async () => {
    if (!schoolID || !userId || !conversationId || !nextCursor || loadingOlder) return;
    setLoadingOlder(true);
    try {
      const enc = encodeURIComponent(conversationId);
      const res = await axios.get(`${API}/api/chat/v1/conversations/${enc}/messages`, {
        params: { schoolID, userId, limit: PAGE_LIMIT, cursor: String(nextCursor) },
      });
      const raw = res.data?.items || [];
      const mapped = raw.map(mapV1Item);
      setItems((prev) => mergeByMessageId(prev, mapped));
      setHasMoreOlder(Boolean(res.data?.hasMore));
      setNextCursor(res.data?.nextCursor != null ? Number(res.data.nextCursor) : null);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Failed to load older';
      setError(msg);
    } finally {
      setLoadingOlder(false);
    }
  }, [conversationId, schoolID, userId, nextCursor, loadingOlder]);

  /** Active thread: initial load when room changes. */
  useEffect(() => {
    let cancelled = false;
    (async () => {
      if (!schoolID || !userId || !conversationId) {
        setItems([]);
        setLoading(false);
        return;
      }
      setLoading(true);
      setItems([]);
      setNextCursor(null);
      setHasMoreOlder(false);
      setDraft('');
      setError(null);
      try {
        const enc = encodeURIComponent(conversationId);
        const res = await axios.get(`${API}/api/chat/v1/conversations/${enc}/messages`, {
          params: { schoolID, userId, limit: PAGE_LIMIT },
        });
        if (cancelled) return;
        const raw = res.data?.items || [];
        setItems(raw.map(mapV1Item));
        setHasMoreOlder(Boolean(res.data?.hasMore));
        setNextCursor(res.data?.nextCursor != null ? Number(res.data.nextCursor) : null);
        await markV1Read(raw, conversationId, schoolID, userId);
        await refreshConversationList();
      } catch (e) {
        if (!cancelled) {
          const msg = e?.response?.data?.message || e.message || 'Failed to load chat';
          setError(msg);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
      setTimeout(scrollBottom, 80);
    })();
    return () => {
      cancelled = true;
    };
  }, [schoolID, userId, conversationId, scrollBottom, refreshConversationList]);

  useEffect(() => {
    if (!schoolID || !userId || !conversationId) return undefined;
    const t = setInterval(() => {
      fetchLatest({ silent: true });
    }, 15000);
    return () => clearInterval(t);
  }, [schoolID, userId, conversationId, fetchLatest]);

  const rows = useMemo(
    () => buildListRows(items, userId, myName),
    [items, userId, myName]
  );

  const onSend = async () => {
    const text = draft.trim();
    if (!text || !schoolID || !userId || !conversationId || sending) return;
    const clientMessageId = `${Date.now()}-${Math.random().toString(16).slice(2)}`;
    setSending(true);
    setError(null);
    try {
      await axios.post(`${API}/api/chat/v1/messages.send`, {
        conversationId,
        schoolID,
        senderId: userId,
        senderName: myName,
        clientMessageId,
        body: text,
        attachments: [],
      });
      setDraft('');
      await fetchLatest({ silent: true });
      setTimeout(scrollBottom, 80);
    } catch (e) {
      const msg = e?.response?.data?.message || e.message || 'Send failed';
      setError(msg);
    } finally {
      setSending(false);
    }
  };

  const onPickRoom = (cid) => {
    if (cid === selectedId) return;
    setSelectedId(cid);
  };

  if (!schoolID || !userId) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="warning">Sign in and select a school to use chat.</Alert>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: 'calc(100vh - 128px)',
        maxHeight: 'calc(100dvh - 128px)',
        minHeight: 360,
        minWidth: 0,
        overflow: 'hidden',
        px: { xs: 1, sm: 2 },
        py: 2,
        maxWidth: 1200,
        mx: 'auto',
        boxSizing: 'border-box',
      }}
    >
      <Box sx={{ flexShrink: 0 }}>
        <Typography variant="h6" gutterBottom>
          Messages
        </Typography>

        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}
      </Box>

      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', md: 'row' },
          gap: 2,
          alignItems: 'stretch',
          flex: 1,
          minHeight: 0,
          minWidth: 0,
        }}
      >
        {/* Desktop sidebar */}
        <Paper
          elevation={0}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            width: { md: 300 },
            flexShrink: 0,
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            overflow: 'hidden',
            minHeight: 0,
            maxHeight: '100%',
          }}
        >
          <Box sx={{ px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider', bgcolor: 'grey.50' }}>
            <Typography variant="subtitle2">Rooms</Typography>
          </Box>
          {listLoading ? (
            <Box display="flex" justifyContent="center" py={4}>
              <CircularProgress size={28} />
            </Box>
          ) : (
            <List disablePadding sx={{ overflowY: 'auto', flex: 1, minHeight: 0 }}>
              {conversations.map((c) => {
                const n = Math.max(0, Number(c.unreadCount) || 0);
                return (
                  <ListItemButton
                    key={c.conversationId}
                    selected={c.conversationId === selectedId}
                    onClick={() => onPickRoom(c.conversationId)}
                    alignItems="flex-start"
                    sx={{ gap: 1, pr: 1.5 }}
                  >
                    <ForumOutlinedIcon
                      sx={{ flexShrink: 0, mt: 0.5, color: 'text.secondary', fontSize: 22 }}
                    />
                    <ListItemText
                      sx={{ flex: '1 1 auto', minWidth: 0, mr: 0.5 }}
                      primary={c.title}
                      secondary={c.subtitle}
                      primaryTypographyProps={{ variant: 'body2', fontWeight: 600 }}
                      secondaryTypographyProps={{ variant: 'caption', noWrap: true }}
                    />
                    <Badge
                      color="error"
                      badgeContent={n}
                      max={99}
                      invisible={n === 0}
                      sx={{
                        flexShrink: 0,
                        mt: 0.25,
                        '& .MuiBadge-badge': {
                          position: 'static',
                          transform: 'none',
                        },
                      }}
                    >
                      <Box component="span" sx={{ width: 10, minHeight: 22 }} aria-hidden />
                    </Badge>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Paper>

        {/* Thread column */}
        <Paper
          elevation={0}
          sx={{
            border: 1,
            borderColor: 'divider',
            borderRadius: 2,
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            minWidth: 0,
            minHeight: 0,
            overflow: 'hidden',
          }}
        >
          {/* Mobile room picker */}
          <Box sx={{ flexShrink: 0, display: { xs: 'block', md: 'none' }, p: 2, pb: 0 }}>
            <FormControl fullWidth size="small">
              <InputLabel id="livechat-room-label">Room</InputLabel>
              <Select
                labelId="livechat-room-label"
                label="Room"
                value={selectedId}
                onChange={(e) => onPickRoom(e.target.value)}
                disabled={listLoading || conversations.length === 0}
                renderValue={(id) => {
                  const c = conversations.find((x) => x.conversationId === id);
                  const n = Math.max(0, Number(c?.unreadCount) || 0);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, width: '100%', overflow: 'hidden' }}>
                      <Typography variant="body2" component="span" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {c?.title || id || 'Room'}
                      </Typography>
                      <Badge
                        color="error"
                        badgeContent={n}
                        max={99}
                        invisible={n === 0}
                        sx={{
                          flexShrink: 0,
                          '& .MuiBadge-badge': {
                            position: 'static',
                            transform: 'none',
                          },
                        }}
                      >
                        <Box component="span" sx={{ width: 8 }} aria-hidden />
                      </Badge>
                    </Box>
                  );
                }}
              >
                {conversations.map((c) => {
                  const n = Math.max(0, Number(c.unreadCount) || 0);
                  return (
                    <MenuItem key={c.conversationId} value={c.conversationId}>
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          width: '100%',
                          gap: 1,
                          justifyContent: 'space-between',
                          pr: 0.5,
                        }}
                      >
                        <Typography variant="body2" component="span" sx={{ flex: 1, minWidth: 0 }} noWrap>
                          {c.title}
                        </Typography>
                        <Badge
                          color="error"
                          badgeContent={n}
                          max={99}
                          invisible={n === 0}
                          sx={{
                            flexShrink: 0,
                            '& .MuiBadge-badge': {
                              position: 'static',
                              transform: 'none',
                            },
                          }}
                        >
                          <Box component="span" sx={{ width: 8 }} aria-hidden />
                        </Badge>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          </Box>

          <Box sx={{ flexShrink: 0, px: 2, py: 1.5, borderBottom: 1, borderColor: 'divider' }}>
            <Typography variant="subtitle1" fontWeight={600}>
              {selectedRoom?.title || 'Chat'}
            </Typography>
            {selectedRoom?.subtitle ? (
              <Typography variant="caption" color="text.secondary" display="block" noWrap title={selectedRoom.subtitle}>
                {selectedRoom.subtitle}
              </Typography>
            ) : null}
            <Typography variant="caption" color="text.secondary" sx={{ wordBreak: 'break-all' }}>
              {conversationId}
            </Typography>
          </Box>

          <Box sx={{ flexShrink: 0, px: 2, py: 1, borderBottom: 1, borderColor: 'divider', display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            <Button size="small" disabled={refreshing || !conversationId} onClick={() => fetchLatest({ silent: false })}>
              Refresh
            </Button>
            {hasMoreOlder && (
              <Button size="small" disabled={loadingOlder} onClick={loadOlder}>
                {loadingOlder ? <CircularProgress size={18} /> : 'Load older'}
              </Button>
            )}
          </Box>

          <Box
            ref={scrollRef}
            sx={{
              flex: '1 1 auto',
              minHeight: 0,
              overflowY: 'auto',
              overflowX: 'hidden',
              WebkitOverflowScrolling: 'touch',
              p: 2,
              bgcolor: 'grey.50',
            }}
          >
            {loading ? (
              <Box display="flex" justifyContent="center" py={6}>
                <CircularProgress />
              </Box>
            ) : rows.length === 0 ? (
              <Typography color="text.secondary" align="center" sx={{ py: 6 }}>
                No messages in this room yet.
              </Typography>
            ) : (
              rows.map((row) => {
                if (row.kind === 'date') {
                  return (
                    <Box key={row.key} sx={{ display: 'flex', justifyContent: 'center', my: 2 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ px: 2, py: 0.5, bgcolor: 'grey.200', borderRadius: 10 }}
                      >
                        {row.label}
                      </Typography>
                    </Box>
                  );
                }
                const m = row.m;
                const bubbleBg = row.own ? 'primary.light' : 'background.paper';
                const align = row.own ? 'flex-end' : 'flex-start';
                const attachmentUrl = m.mediaUrl || (m.attachments && m.attachments[0]?.url);

                return (
                  <Box key={row.key} sx={{ display: 'flex', justifyContent: align, mb: 1.5 }}>
                    <Paper
                      elevation={1}
                      sx={{
                        maxWidth: '82%',
                        px: 1.5,
                        py: 1,
                        bgcolor: bubbleBg,
                        borderRadius: 2,
                      }}
                    >
                      {!row.own && (
                        <Typography variant="caption" fontWeight={600} display="block" gutterBottom>
                          {m.sender || m.senderName || 'Staff'}
                        </Typography>
                      )}
                      {attachmentUrl ? (
                        m.mediaType === 'image' ? (
                          <Box
                            component="img"
                            src={attachmentUrl.startsWith('http') ? attachmentUrl : `${API}${attachmentUrl}`}
                            alt=""
                            sx={{
                              maxWidth: '100%',
                              borderRadius: 1,
                              display: 'block',
                              mb: m.message ? 1 : 0,
                            }}
                          />
                        ) : (
                          <MuiLink
                            href={attachmentUrl.startsWith('http') ? attachmentUrl : `${API}${attachmentUrl}`}
                            target="_blank"
                            rel="noopener noreferrer"
                            display="block"
                            sx={{ mb: m.message ? 1 : 0 }}
                          >
                            {m.fileName || 'Attachment'}
                          </MuiLink>
                        )
                      ) : null}
                      {m.message ? (
                        <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                          {m.message}
                        </Typography>
                      ) : null}
                      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                        {m.sentTime
                          ? new Date(m.sentTime).toLocaleTimeString(undefined, {
                              hour: 'numeric',
                              minute: '2-digit',
                            })
                          : ''}
                      </Typography>
                    </Paper>
                  </Box>
                );
              })
            )}
          </Box>

          <Divider flexItem sx={{ flexShrink: 0 }} />

          <Box
            sx={{
              flexShrink: 0,
              p: 2,
              display: 'flex',
              gap: 1,
              alignItems: 'flex-end',
              bgcolor: 'background.paper',
              borderTop: 1,
              borderColor: 'divider',
            }}
          >
            <TextField
              fullWidth
              multiline
              maxRows={4}
              placeholder="Type a message…"
              value={draft}
              disabled={sending || !conversationId}
              onChange={(e) => setDraft(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  onSend();
                }
              }}
            />
            <Button variant="contained" disabled={sending || !draft.trim() || !conversationId} onClick={onSend}>
              {sending ? <CircularProgress size={22} color="inherit" /> : 'Send'}
            </Button>
          </Box>
        </Paper>
      </Box>

      <Typography variant="caption" color="text.secondary" sx={{ flexShrink: 0, display: 'block', mt: 1 }}>
        Office announcements from SIS use <strong>Messages</strong> in the menu.
      </Typography>
    </Box>
  );
}
